const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

const CRUD_SEARCH_CONTROL_SELECTOR = [
  "[data-crud-search] input:not([disabled]):not([type='hidden'])",
  "input[data-crud-search]:not([disabled]):not([type='hidden'])",
  "input[type='search']:not([disabled])",
  "input[name='search']:not([disabled])",
  ".crud-listagem-filters input:not([disabled]):not([type='hidden'])",
  ".crud-listagem-filters textarea:not([disabled])",
  ".crud-listagem-filters select:not([disabled])",
  ".crud-listagem-filters [role='combobox'][tabindex]:not([aria-disabled='true'])",
  ".card-header-padding input:not([disabled]):not([type='hidden'])",
  ".card-header-padding textarea:not([disabled])",
  ".card-header-padding select:not([disabled])",
  ".card-header-padding [role='combobox'][tabindex]:not([aria-disabled='true'])",
].join(",");

function cleanControlLabel(value) {
  return String(value || "")
    .replace(/\s*\*\s*$/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function humanizeControlName(value) {
  const normalized = cleanControlLabel(value)
    .replace(/^input[-_]?/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .trim();
  if (!normalized) return "";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function isElementVisible(element) {
  if (!element || !element.isConnected) return false;
  const view = element.ownerDocument?.defaultView;
  const style = view?.getComputedStyle(element);

  return (
    (!style || (style.display !== "none" && style.visibility !== "hidden")) &&
    element.getClientRects().length > 0
  );
}

export function isEditableKeyboardTarget(target) {
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(
    target.closest(
      "input, textarea, select, [contenteditable=''], [contenteditable='true']"
    )
  );
}

export function isCrudSearchShortcut(event) {
  const key = String(event?.key || "").toLowerCase();
  const code = String(event?.code || "");
  return Boolean(
    event &&
      (event.ctrlKey || event.metaKey) &&
      !event.shiftKey &&
      event.altKey &&
      !event.repeat &&
      (code === "KeyS" || key === "s")
  );
}

export function getCrudSearchControls(container) {
  if (!container?.querySelectorAll) return [];
  const controls = Array.from(
    container.querySelectorAll(CRUD_SEARCH_CONTROL_SELECTOR)
  );
  return controls.filter(
    (control, index) =>
      controls.indexOf(control) === index &&
      !(
        control.matches?.("[role='combobox']") &&
        !control.matches?.("input, textarea, select") &&
        control.querySelector?.("input, textarea, select")
      ) &&
      !["checkbox", "radio", "submit", "button"].includes(
        String(control.type || "").toLowerCase()
      ) &&
      control.getAttribute("aria-hidden") !== "true" &&
      isElementVisible(control)
  );
}

export function getCrudSearchControlLabel(control, fallback = "") {
  if (!control) return cleanControlLabel(fallback);

  const explicitLabel =
    control.getAttribute?.("data-crud-search-label") ||
    control
      .closest?.("[data-crud-search-label]")
      ?.getAttribute?.("data-crud-search-label");
  const directLabel = explicitLabel || control.getAttribute?.("aria-label");
  if (cleanControlLabel(directLabel)) return cleanControlLabel(directLabel);

  const id = control.getAttribute?.("id");
  if (id && control.ownerDocument?.querySelectorAll) {
    const associatedLabel = Array.from(
      control.ownerDocument.querySelectorAll("label[for]")
    ).find((label) => label.getAttribute("for") === id);
    if (cleanControlLabel(associatedLabel?.textContent)) {
      return cleanControlLabel(associatedLabel.textContent);
    }
  }

  const fieldWrapper = control.closest?.(
    ".v-input, .v-text-field, .v-select, .v-autocomplete"
  );
  const vuetifyLabel = fieldWrapper?.querySelector?.(".v-label");
  if (cleanControlLabel(vuetifyLabel?.textContent)) {
    return cleanControlLabel(vuetifyLabel.textContent);
  }

  const placeholder = control.getAttribute?.("placeholder");
  if (cleanControlLabel(placeholder)) return cleanControlLabel(placeholder);

  const title = control.getAttribute?.("title");
  if (cleanControlLabel(title)) return cleanControlLabel(title);

  return (
    humanizeControlName(
      control.getAttribute?.("name") || control.getAttribute?.("id")
    ) || cleanControlLabel(fallback)
  );
}

export function consumeKeyboardEvent(event, stopImmediate = false) {
  event.preventDefault();
  event.stopPropagation();
  if (stopImmediate && typeof event.stopImmediatePropagation === "function") {
    event.stopImmediatePropagation();
  }
}

export function getTopActiveDialog(doc) {
  const resolvedDocument =
    doc || (typeof document !== "undefined" ? document : null);
  if (!resolvedDocument) return null;
  const dialogs = Array.from(
    resolvedDocument.querySelectorAll(".v-dialog--active")
  ).filter(isElementVisible);
  return dialogs[dialogs.length - 1] || null;
}

export function isElementInTopDialog(element) {
  if (!element) return false;
  const topDialog = getTopActiveDialog(element.ownerDocument);
  return Boolean(topDialog && topDialog.contains(element));
}

export function isKeyboardScopeActive(root, selector, eventTarget) {
  if (!root || !selector || !isElementVisible(root)) return false;

  const doc = root.ownerDocument;
  const topDialog = getTopActiveDialog(doc);
  const rootDialog = root.closest?.(".v-dialog--active");

  if (topDialog && rootDialog !== topDialog && !topDialog.contains(root)) {
    return false;
  }

  const targetScope = eventTarget?.closest?.(selector);
  if (targetScope) return targetScope === root;

  const visibleScopes = Array.from(doc.querySelectorAll(selector)).filter(
    isElementVisible
  );
  return visibleScopes[visibleScopes.length - 1] === root;
}

export function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) =>
      isElementVisible(element) &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.getAttribute("aria-disabled") !== "true"
  );
}

export function trapTabWithin(container, event) {
  if (!container || event.key !== "Tab") return false;
  const focusable = getFocusableElements(container);
  if (!focusable.length) return false;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const activeElement = container.ownerDocument.activeElement;

  if (event.shiftKey && activeElement === first) {
    consumeKeyboardEvent(event);
    last.focus();
    return true;
  }

  if (!event.shiftKey && activeElement === last) {
    consumeKeyboardEvent(event);
    first.focus();
    return true;
  }

  return false;
}

export function trapTabInOwningDialog(root, event) {
  const dialog = root?.closest?.(".v-dialog--active");
  if (!dialog || getTopActiveDialog(root.ownerDocument) !== dialog) {
    return false;
  }
  return trapTabWithin(dialog, event);
}

export function resolveComponentElement(reference) {
  const resolved = Array.isArray(reference) ? reference[0] : reference;
  return resolved?.$el || resolved || null;
}

export function activateControl(reference) {
  const element = resolveComponentElement(reference);
  if (
    !isElementVisible(element) ||
    element.disabled ||
    element.getAttribute("aria-disabled") === "true" ||
    element.classList.contains("v-btn--disabled") ||
    element.classList.contains("v-btn--loading")
  ) {
    return false;
  }

  element.click();
  return true;
}

export function focusControl(reference) {
  const component = Array.isArray(reference) ? reference[0] : reference;
  const element = resolveComponentElement(component);
  if (!isElementVisible(element)) return false;

  if (typeof component?.focus === "function") {
    component.focus();
    return true;
  }

  const focusTarget = element.matches?.(FOCUSABLE_SELECTOR)
    ? element
    : element.querySelector?.(FOCUSABLE_SELECTOR);
  if (!focusTarget || !isElementVisible(focusTarget)) return false;
  focusTarget.focus();
  return true;
}

export function shouldPreventNamedFieldEnter(event) {
  if (event.key !== "Enter" || event.ctrlKey || event.metaKey) return false;
  const name = String(event.target?.name || "");
  return ["cBarras", "searchProdutos", "searchProdutosPedido"].some(
    (fragment) => name.includes(fragment)
  );
}
